/** One caller-bound database snapshot; backend availability is the only cached state. */
import { getSupabaseClient } from '../utils/supabase/client';
import type { GlobalSearchPage, GlobalSearchRequest, GlobalSearchResult, GlobalSearchType } from './globalSearch';

export interface SearchRecord extends GlobalSearchResult { clientName?: string; projectName?: string }
export interface SearchSnapshot extends GlobalSearchPage { results: SearchRecord[] }
interface Lookup { projectIds?: string[]; taskIds?: string[] }
const types: GlobalSearchType[] = ['project', 'task'];
const invalid = () => new Error('Search permissions could not be verified.');
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
let missingUntil = 0;
export const isGlobalSearchLegacyBackend = () => Date.now() < missingUntil;
const checkAbort = (signal?: AbortSignal) => { if (signal?.aborted) throw new DOMException('Aborted', 'AbortError'); };

async function checkActor(request: GlobalSearchRequest) {
  checkAbort(request.signal);
  const { data, error } = await getSupabaseClient().auth.getSession();
  if (error || data.session?.user.id !== request.actorId) throw invalid();
}

/** Only a missing-function rollout can fall back. Errors and malformed authority fail closed. */
export async function searchGlobalRpc(request: GlobalSearchRequest,
  cursors: Partial<Record<GlobalSearchType, number>> = {}, sourceTypes: GlobalSearchType[] = types,
  lookup: Lookup = {}): Promise<SearchSnapshot | null> {
  await checkActor(request);
  if (isGlobalSearchLegacyBackend()) return null;
  const response = await getSupabaseClient().rpc('global_search_records', {
    p_workspace_id: request.workspaceId, p_query: request.query.trim().slice(0, 200),
    p_types: sourceTypes, p_project_offset: cursors.project ?? 0, p_task_offset: cursors.task ?? 0,
    p_project_ids: lookup.projectIds ?? null, p_task_ids: lookup.taskIds ?? null,
  }).abortSignal(request.signal!);
  await checkActor(request);
  if (response.error) {
    if (response.error.code === 'PGRST202') {
      missingUntil = Date.now() + 30_000;
      return null;
    }
    throw invalid();
  }
  const data: unknown = response.data;
  if (!object(data) || data.actorId !== request.actorId || data.workspaceId !== request.workspaceId
    || typeof data.crossWorkspace !== 'boolean' || !Array.isArray(data.results) || data.results.length > 40
    || !object(data.cursors) || !object(data.hasMore) || !Array.isArray(data.unavailable)
    || data.unavailable.some(value => !types.includes(value as GlobalSearchType))) throw invalid();
  const next = {} as Record<GlobalSearchType, number>;
  const more = {} as Record<GlobalSearchType, boolean>;
  for (const type of types) {
    const cursor = data.cursors[type];
    if (typeof cursor !== 'number' || !Number.isSafeInteger(cursor) || cursor < (cursors[type] ?? 0)
      || cursor > 100_080 || typeof data.hasMore[type] !== 'boolean') throw invalid();
    next[type] = cursor;
    more[type] = data.hasMore[type] as boolean;
  }
  const seen = new Set<string>();
  const counts = { project: 0, task: 0 };
  const results: SearchRecord[] = data.results.map(value => {
    if (!object(value) || !types.includes(value.type as GlobalSearchType) || !sourceTypes.includes(value.type as GlobalSearchType)
      || typeof value.id !== 'string' || !value.id || typeof value.label !== 'string' || !value.label
      || (value.description != null && typeof value.description !== 'string')
      || (value.projectId != null && typeof value.projectId !== 'string')
      || (value.workspaceId != null && typeof value.workspaceId !== 'string')
      || (value.clientName != null && typeof value.clientName !== 'string')
      || (value.projectName != null && typeof value.projectName !== 'string')) throw invalid();
    const type = value.type as GlobalSearchType;
    if (++counts[type] > 20 || seen.has(`${type}:${value.id}`)) throw invalid();
    seen.add(`${type}:${value.id}`);
    if (type === 'project' && (value.projectId !== value.id || !value.workspaceId || !value.clientName)) throw invalid();
    if (value.projectId && (!value.workspaceId || (!data.crossWorkspace && value.workspaceId !== request.workspaceId))) throw invalid();
    if (lookup.projectIds && type === 'project' && !lookup.projectIds.includes(value.id)) throw invalid();
    if (lookup.taskIds && type === 'task' && !lookup.taskIds.includes(value.id)) throw invalid();
    return { id: value.id, type, label: value.label,
      ...(typeof value.description === 'string' ? { description: value.description } : {}),
      ...(typeof value.projectId === 'string' ? { projectId: value.projectId } : {}),
      ...(typeof value.workspaceId === 'string' ? { workspaceId: value.workspaceId } : {}),
      ...(typeof value.clientName === 'string' ? { clientName: value.clientName } : {}),
      ...(typeof value.projectName === 'string' ? { projectName: value.projectName } : {}),
    };
  });
  return { results, cursors: { ...next, backend: 'rpc' }, hasMore: more, unavailable: data.unavailable as GlobalSearchType[] };
}
